import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { RiskProfile } from '@/lib/types'
import { cn, formatCurrency } from '@/lib/utils'

interface RiskProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedProfile: RiskProfile
  onSelectProfile: (profile: RiskProfile) => void
  currentBalance: number
}

// Multiplicadores para meta diária baseado no perfil de risco
const DAILY_GOAL_MULTIPLIERS = {
  conservative: { divisor: 100, percent: 1 },
  moderate: { divisor: 50, percent: 2 },
  aggressive: { divisor: 20, percent: 5 },
} as const

// SVG Icons for each profile (chess pieces)
const ConservativeIcon = () => (
  <svg width="35" height="63" viewBox="0 0 35 63" fill="none" xmlns="http://www.w3.org/2000/svg" className="-ml-1">
    <g filter="url(#ConservativeStrategyIcon_a)">
      <path fill="url(#ConservativeStrategyIcon_b)" fillRule="evenodd" d="M17.08 23.448a.824.824 0 0 0 .583.242l6.387-.004a.824.824 0 0 0 0-1.65h-.107l.08-.07c1.396-1.31 1.826-3.807 1.27-5.085a23.075 23.075 0 0 0-2.545-4.236c-.383-.549-.809-1.16-1.29-1.878l-.027-.04.043-.02a1.432 1.432 0 0 0 .75-1.336A1.324 1.324 0 0 0 20.83 8c-.77 0-1.247.525-1.247 1.37 0 .666.221 1.116.655 1.337l.044.02-.028.039c-.156.235-.329.487-.52.767a177.37 177.37 0 0 0-.666.974c-.2.292-.38.555-.51.752l1.317 2.895c.004 0 .134.237.044.395a.304.304 0 0 1-.28.126c-.403 0-.497-.442-.497-.442l-1.038-2.26c-.173.276-.355.544-.54.816a12.544 12.544 0 0 0-1.227 2.095c-.533 1.227-.051 3.767 1.353 5.085l.079.07h-.107a.824.824 0 0 0-.583 1.408Zm1.042 6.03c-.146 3.108-1.878 6.953-3.187 9.608h11.889c-1.176-2.445-3.1-6.477-3.25-9.609h-5.452Zm-5.346 23.351a5.223 5.223 0 0 1 .134-1.211c.121-.296.301-.564.529-.789h14.48c.215.24.383.516.498.817.086.389.137.785.15 1.183h-15.79Zm-.367-6.714a8.178 8.178 0 0 0 1.116 4.34l14.292.03a7.664 7.664 0 0 0 1.183-4.2c-.07-1.52-.841-3.014-1.523-4.334l-.003-.006-.233-.457h-12.99c-.138.284-.3.588-.47.903l-.006.013c-.606 1.133-1.295 2.422-1.366 3.71Zm2.137-6.718h12.509a.958.958 0 0 1 .852.793.911.911 0 0 1-.604.951H14.286a.86.86 0 0 1-.564-.93.946.946 0 0 1 .824-.813Zm1.469-11.418c-.068.655.67 1.08.76 1.08v-.003h8.415a1.077 1.077 0 0 0 .726-1.022 1.408 1.408 0 0 0-.888-1.183h-1.03v-2.82h-6.31v2.82h-.742a1.322 1.322 0 0 0-.931 1.128Z" clipRule="evenodd" />
    </g>
    <defs>
      <linearGradient id="ConservativeStrategyIcon_b" x1="29.98" x2="12.78" y1="16.143" y2="18.211" gradientUnits="userSpaceOnUse">
        <stop stopColor="#fff" />
        <stop offset="1" stopColor="#A4C198" />
      </linearGradient>
      <filter id="ConservativeStrategyIcon_a" width="33.659" height="61.892" x=".675" y=".535" colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse">
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
        <feOffset dx="-3.199" dy="1.066" />
        <feGaussianBlur stdDeviation="4.266" />
        <feColorMatrix values="0 0 0 0 0.16949 0 0 0 0 0.366667 0 0 0 0 0.0825 0 0 0 0.25 0" />
        <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_4892_50178" />
        <feBlend in="SourceGraphic" in2="effect1_dropShadow_4892_50178" result="shape" />
        <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
        <feOffset dx="-1.726" dy=".561" />
        <feGaussianBlur stdDeviation=".907" />
        <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
        <feColorMatrix values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" />
        <feBlend in2="shape" result="effect2_innerShadow_4892_50178" />
      </filter>
    </defs>
  </svg>
)

const ModerateIcon = () => (
  <svg width="39" height="60" viewBox="0 0 39 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_di_1772_25109)">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.9115 10.7518C10.0406 15.8816 11.5396 32.7998 14.5122 36.868H28.0629L28.8231 30.7487C25.6297 28.1711 22.2579 23.3555 22.3768 18.8287C22.398 18.3658 22.6146 18.2044 22.8015 18.2044C22.9883 18.2044 23.2049 18.3743 23.2049 18.697C23.2134 18.9476 23.3493 21.16 25.3027 21.16C25.5901 21.1548 25.8757 21.112 26.152 21.0326C26.8496 20.8323 27.5707 20.7252 28.2965 20.7141C28.7838 20.7034 29.269 20.7826 29.7276 20.9477C30.1359 21.1038 30.5149 21.3278 30.8487 21.6102C31.1023 21.8204 31.3769 22.004 31.6682 22.158C32.2518 21.5717 32.7429 20.9001 33.1248 20.1663C33.6811 19.0877 34.0803 18.3064 32.5175 17.2532C30.8617 16.0102 29.5267 14.3898 28.6235 12.5268C28.3073 11.6241 27.6814 10.8628 26.8569 10.3781H26.8229V10.3398C26.8556 10.2418 26.8945 10.147 26.9342 10.0499L26.9343 10.0498L26.9546 10.0001C27.1796 9.46506 27.409 8.91301 26.3643 8C25.9083 8.32674 25.4908 8.70428 25.1201 9.12533V9.15081H25.0903C24.3729 8.93588 23.6268 8.83135 22.8779 8.84082C20.7388 8.84184 18.6532 9.50982 16.9115 10.7518ZM12.3885 49.3237C12.2787 49.7104 12.2244 50.1107 12.2272 50.5127L30.0244 50.5042C30.0008 50.1112 29.9382 49.7214 29.8376 49.3407C29.6829 49.0424 29.4806 48.7714 29.2388 48.5381H13.0382C12.7678 48.7503 12.5462 49.0182 12.3885 49.3237ZM13.1454 48.1435C12.2994 46.8776 11.9031 45.3642 12.0201 43.846C12.1178 42.398 13.0308 40.9202 13.7654 39.7354C13.8631 39.5783 13.9565 39.4254 14.0415 39.2768H28.3905C29.036 40.3002 30.1189 42.2791 30.2293 44.1688C30.3413 45.5856 29.9421 46.9957 29.1039 48.1435H13.1454ZM13.6617 38.5887C13.7594 38.7505 13.9127 38.8711 14.093 38.928H28.4675C28.651 38.8744 28.8077 38.7539 28.9068 38.5904C29.0058 38.4269 29.04 38.2322 29.0026 38.0448C28.9636 37.8438 28.859 37.6615 28.7054 37.5262C28.5517 37.391 28.3575 37.3105 28.1533 37.2974H14.4115C14.2066 37.3107 14.012 37.3918 13.8582 37.5279C13.7045 37.664 13.6003 37.8473 13.5622 38.049C13.5286 38.235 13.564 38.4269 13.6617 38.5887Z" fill="url(#paint0_linear_1772_25109)" />
    </g>
    <defs>
      <filter id="filter0_di_1772_25109" x="0.574177" y="0.729022" width="38.2375" height="59.1321" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dx="-3.11613" dy="1.03871" />
        <feGaussianBlur stdDeviation="4.15484" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.458824 0 0 0 0 0.6 0 0 0 0 0.705882 0 0 0 0.25 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1772_25109" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1772_25109" result="shape" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dx="-1.6807" dy="0.546093" />
        <feGaussianBlur stdDeviation="0.883597" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" />
        <feBlend mode="normal" in2="shape" result="effect2_innerShadow_1772_25109" />
      </filter>
      <linearGradient id="paint0_linear_1772_25109" x1="34.8936" x2="12.7673" y1="15.7218" y2="19.3776" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="#B6CEDF" />
      </linearGradient>
    </defs>
  </svg>
)

const AggressiveIcon = () => (
  <svg width="35" height="62" viewBox="0 0 35 62" fill="none" xmlns="http://www.w3.org/2000/svg" className="-ml-1">
    <g filter="url(#filter0_di_1757_35584)">
      <path fillRule="evenodd" clipRule="evenodd" d="M19.6024 12.4797C20.0665 12.7898 20.6121 12.9553 21.1703 12.9553C21.9187 12.9553 22.6366 12.6579 23.1658 12.1287C23.6951 11.5994 23.9924 10.8816 23.9924 10.1332C23.9924 9.57499 23.8269 9.02936 23.5168 8.56527C23.2067 8.10117 22.7659 7.73946 22.2502 7.52586C21.7346 7.31226 21.1671 7.25637 20.6197 7.36526C20.0723 7.47415 19.5694 7.74294 19.1747 8.13762C18.78 8.53229 18.5113 9.03515 18.4024 9.58258C18.2935 10.13 18.3494 10.6975 18.563 11.2131C18.7766 11.7288 19.1383 12.1696 19.6024 12.4797ZM14.6938 40.8765L14.5039 41.2416L27.6434 41.2307L27.5046 40.9751L27.4956 40.9587C25.8415 37.9309 24.2764 35.0663 23.8209 28.062H18.3081C17.8591 35.0087 16.3918 37.7288 14.6944 40.8753L14.6938 40.8765ZM12.9489 50.9062H28.8631C29.097 51.0973 29.2906 51.3331 29.4326 51.5998C29.5268 51.9307 29.5831 52.2712 29.6005 52.6148H12.2115C12.2087 52.2624 12.2591 51.9116 12.3612 51.5743C12.5055 51.311 12.7062 51.0828 12.9489 50.9062ZM12.0652 47.066C11.9624 48.3356 12.3187 49.6 13.0692 50.6292L28.7387 50.6256C29.4768 49.6968 29.8344 48.5223 29.739 47.3398C29.6404 45.7699 28.6766 44.1307 28.1034 43.2837H13.8651L13.8447 43.3138C13.7736 43.4188 13.6967 43.5322 13.6168 43.6488L13.594 43.6836C12.948 44.6668 12.1518 45.8789 12.0652 47.066ZM14.1972 41.5703H27.9026L27.8953 41.574C28.0808 41.5797 28.2593 41.646 28.4037 41.7626C28.5481 41.8793 28.6503 42.04 28.6948 42.2202C28.7227 42.3908 28.6852 42.5657 28.5898 42.7098C28.4943 42.854 28.348 42.9568 28.18 42.9978H13.9125C13.7441 42.9576 13.5972 42.8549 13.5016 42.7106C13.406 42.5662 13.3689 42.3909 13.3977 42.2202C13.4415 42.0393 13.5435 41.8778 13.6879 41.7604C13.8323 41.643 14.0112 41.5763 14.1972 41.5703ZM15.9642 27.786H17.5194H25.9164C26.1741 27.6978 26.3971 27.5301 26.5533 27.307C26.7094 27.0839 26.7907 26.8169 26.7853 26.5447C26.759 26.2304 26.6456 25.9298 26.4579 25.6763C26.2703 25.4229 26.0158 25.2268 25.7229 25.1099H24.2626L24.2224 25.088V25.0442C23.9632 20.8019 26.1136 17.2423 27.6469 16.147C27.8733 15.99 28.9539 15.143 28.1726 14.0295C28.0426 13.8342 27.8662 13.6742 27.6591 13.564C27.452 13.4538 27.2207 13.3967 26.9861 13.3979C26.8717 13.3974 26.7577 13.4108 26.6466 13.4381C26.5199 13.487 26.3978 13.5469 26.2815 13.617C25.8845 13.8745 25.4284 14.0266 24.9562 14.0587C24.316 14.0347 23.6967 13.8241 23.1746 13.4527L22.9848 13.303H22.9629C22.9331 13.3291 22.905 13.3572 22.8789 13.387C22.3483 13.8092 21.7038 14.0634 21.0279 14.1171C20.305 14.1171 19.6333 13.7046 19.071 13.292C18.497 13.7394 17.8006 14.0017 17.074 14.0441C16.6108 14.0121 16.1629 13.8653 15.7707 13.617C15.6551 13.5456 15.5328 13.4857 15.4056 13.4381C15.2957 13.4108 15.1829 13.3973 15.0697 13.3979C14.835 13.3964 14.6037 13.4533 14.3965 13.5636C14.1894 13.6738 14.0129 13.834 13.8832 14.0295C13.0982 15.143 14.1825 15.99 14.4052 16.147C15.9496 17.2313 18.1036 20.8201 17.8297 25.0734V25.1099H16.1431C15.8468 25.2051 15.585 25.3853 15.3903 25.6281C15.1956 25.8708 15.0765 26.1654 15.0478 26.4753C14.9675 27.2712 15.851 27.786 15.9642 27.786Z" fill="url(#paint0_linear_1757_35584)" />
    </g>
    <defs>
      <filter id="filter0_di_1757_35584" x="0.683514" y="0.0791841" width="34.236" height="61.8337" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dx="-3.09937" dy="1.03312" />
        <feGaussianBlur stdDeviation="4.13249" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.458333 0 0 0 0 0.193111 0 0 0 0 0.0439236 0 0 0 0.25 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1757_35584" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1757_35584" result="shape" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dx="-1.67166" dy="0.543154" />
        <feGaussianBlur stdDeviation="0.878842" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" />
        <feBlend mode="normal" in2="shape" result="effect2_innerShadow_1757_35584" />
      </filter>
      <linearGradient id="paint0_linear_1757_35584" x1="30.7991" x2="12.4771" y1="15.5398" y2="17.8665" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="#E7ABA3" />
      </linearGradient>
    </defs>
  </svg>
)

// Risk level indicator component
const RiskLevelIndicator = ({ level }: { level: 'low' | 'medium' | 'high' }) => {
  const colors = {
    low: 'text-muted-foreground',
    medium: 'text-amber-500',
    high: 'text-rose-500',
  }

  const labels = {
    low: 'Risco baixo',
    medium: 'Risco médio',
    high: 'Risco alto',
  }

  return (
    <div className={cn('flex items-center gap-1.5', colors[level])}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Centro - acende em medium e high */}
        <path
          opacity={level === 'medium' || level === 'high' ? '1' : '0.3'}
          d="M4 5.13458L8 12L11.9258 5.13458C10.7996 4.41923 9.47718 4 8 4C6.3883 4 5.06927 4.44176 4 5.13458Z"
          fill={level === 'medium' || level === 'high' ? 'currentColor' : 'var(--muted-foreground)'}
        />
        {/* Direita - acende apenas em high */}
        <path
          opacity={level === 'high' ? '1' : '0.3'}
          d="M9.15234 11.9987H15.9996C15.6917 9.53566 14.5302 7.22509 12.7364 5.72656L9.15234 11.9987Z"
          fill={level === 'high' ? 'currentColor' : 'var(--muted-foreground)'}
        />
        {/* Esquerda - sempre acende (low, medium, high) */}
        <path
          opacity="1"
          d="M3.19591 5.73828C1.12321 7.53368 0.211777 10.3043 0 11.9985H6.84385L3.19591 5.73828Z"
          fill="currentColor"
        />
      </svg>
      <span className="text-xs font-medium">{labels[level]}</span>
    </div>
  )
}

const PROFILE_CONFIG = {
  conservative: {
    icon: ConservativeIcon,
    label: 'Conservador',
    riskLevel: 'low' as const,
    bgColor: 'bg-emerald-600',
    borderColor: 'border-emerald-500',
  },
  moderate: {
    icon: ModerateIcon,
    label: 'Moderado',
    riskLevel: 'medium' as const,
    bgColor: 'bg-blue-600',
    borderColor: 'border-blue-500',
  },
  aggressive: {
    icon: AggressiveIcon,
    label: 'Agressivo',
    riskLevel: 'high' as const,
    bgColor: 'bg-orange-600',
    borderColor: 'border-orange-500',
  },
}

export function RiskProfileModal({
  open,
  onOpenChange,
  selectedProfile,
  onSelectProfile,
  currentBalance,
}: RiskProfileModalProps) {
  const handleSelect = (profile: RiskProfile) => {
    onSelectProfile(profile)
    onOpenChange(false)
  }

  // Calcular valor de entrada para cada perfil baseado no saldo atual
  const getEntryValue = (profile: RiskProfile) => {
    const multiplier = DAILY_GOAL_MULTIPLIERS[profile]
    return currentBalance / multiplier.divisor
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={() => onOpenChange(false)}
        />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-foreground">
              Defina seu Perfil
            </Dialog.Title>
            <Dialog.Close className="rounded-lg p-1.5 hover:bg-muted transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </Dialog.Close>
          </div>

          <div className="space-y-3">
            {(Object.entries(PROFILE_CONFIG) as [RiskProfile, typeof PROFILE_CONFIG[RiskProfile]][]).map(
              ([key, config]) => {
                const Icon = config.icon
                const isSelected = selectedProfile === key
                const entryValue = getEntryValue(key)
                const multiplier = DAILY_GOAL_MULTIPLIERS[key]

                return (
                  <button
                    key={key}
                    onClick={() => handleSelect(key)}
                    className={cn(
                      'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200',
                      'hover:bg-muted/50',
                      isSelected
                        ? 'border-emerald-500/40 bg-emerald-500/5'
                        : 'border-border/50 bg-card hover:border-border'
                    )}
                  >
                    {/* Icon */}
                    <div
                      className={cn(
                        'flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center',
                        config.bgColor
                      )}
                    >
                      <Icon />
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left">
                      <p className="text-base font-semibold text-foreground mb-1">{config.label}</p>
                      <RiskLevelIndicator level={config.riskLevel} />
                      <p className="text-xs text-muted-foreground mt-1">
                        Entrada: <span className="font-semibold text-foreground">{formatCurrency(entryValue)}</span>
                        <span className="text-muted-foreground/70"> ({multiplier.percent}% do saldo)</span>
                      </p>
                    </div>
                  </button>
                )
              }
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            Escolha o perfil que melhor se adapta ao seu estilo de operação
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
